import type { Presentation, PresentationV2 } from '../presentation/types.ts';
import type { Resolved } from '../presentation/bindings.ts';
import { createArtResolver, type ArtResolver } from '../faces/art.ts';
import { artAsset } from './assets.ts';

const resolvers = new WeakMap<object, Map<number, ArtResolver>>();

/**
 * The art an observed object's face draws in an art slot (default `portrait`), as an image for a small portrait.
 * Null when the presentation declares no such slot, nothing is assigned to the object's catalog key, kind or tags, or the object is face down.
 */
export function objectPortrait(
  presentation: Presentation | PresentationV2 | null | undefined,
  object: { kind: string; catalogKey: string | null; tags: string[]; faceDown: boolean },
  slot = 'portrait',
  width = 80,
): Resolved | null {
  if (!presentation || !('format' in presentation) || !presentation.art) return null;
  let byWidth = resolvers.get(presentation);
  if (!byWidth) resolvers.set(presentation, byWidth = new Map());
  let resolver = byWidth.get(width);
  if (!resolver) byWidth.set(width, resolver = createArtResolver(presentation.atlases ?? null, presentation.art, id => artAsset(presentation, id, 4096, 1), id => artAsset(presentation, id, width)));
  const value = resolver(object, slot).value;
  if (!value || value.kind !== 'frame') return null;
  const whole = value.frame.x === 0 && value.frame.y === 0 && value.frame.w === value.sheet.w && value.frame.h === value.sheet.h;
  return whole ? { kind: 'image', url: value.url } : { kind: 'image', url: value.url, frame: value.frame, sheet: value.sheet };
}
